<template>
  <div class="generated-solution">
    <!-- 顶部导航 -->
  <div class="top-nav">
    <div class="back-header">
      <el-button type="text" size="small" @click="back">
        <el-icon><ArrowLeft /></el-icon>
        返回分析结果
      </el-button>
    </div>
    <el-button type="primary" size="small" class="enter-btn" :disabled="!enterLoading || !hasSolutionContent" @click="$emit('enter-achievement-library', localSolutionSections)">进入成果库生成</el-button>
  </div>

    <!-- 方案内容区域 -->
    <div class="solution-content">
      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-tip">
          <el-icon class="loading-tip__icon"><Loading /></el-icon>
          <p class="loading-tip__title">方案生成中...</p>
          <p class="loading-tip__hint">正在根据分析结果生成完整解决方案，通常需要 1-3 分钟，最长等待 5 分钟</p>
          <p class="loading-tip__elapsed">已等待 {{ elapsedSeconds }} 秒</p>
        </div>
        <el-skeleton :rows="7" animated />
      </div>
      
      <!-- 错误信息：显示失败原因 + 手动重试按钮 -->
      <div v-else-if="errorMessage" class="error-container">
        <el-alert :title="errorMessage" type="error" show-icon :closable="false" />
        <div class="error-actions">
          <el-button type="primary" @click="generateReport">
            <el-icon><Refresh /></el-icon>
            重新生成
          </el-button>
          <el-button @click="back">返回分析结果</el-button>
        </div>
      </div>
      
      <!-- 方案部分循环渲染 -->
      <div 
        v-else
        v-for="(section, index) in localSolutionSections" 
        :key="section.key"
        class="solution-section"
      >
        <div class="section-header">
          <h3 class="section-title">{{ index + 1 }}. {{ section.title }}</h3>
        </div>
        <div class="section-text">
          <!-- 直接编辑：章节始终是富文本编辑器，改动自动保存（见 handleEditorChange） -->
          <div class="editor-container">
            <Toolbar
              :editor="editorInstances[section.key]"
              :default-config="toolbarConfig"
              :mode="'default'"
            />
            <Editor
              v-model="editorContents[section.key]"
              :default-config="editorConfig"
              :mode="'default'"
              @on-change="(editor) => handleEditorChange(section.key, editor)"
              @on-before-destroy="(editor) => handleEditorBeforeDestroy(section.key, editor)"
              @on-created="(editor) => editorInstances[section.key] = editor"
              @custom-paste="customPaste"
            />
          </div>
        </div>
      </div>
      
      <!-- 本案所参考的所有方案和专利 -->
      <div v-if="showReference &&(selectedTechPaths.length > 0 || selectedPatentsData.length > 0)" class="reference-section">
        <h3 class="reference-title">本案所参考的所有方案和专利</h3>
        <div class="reference-list">
          <!-- 显示选中的技术路径 -->
          <div 
            v-for="(techPath, index) in selectedTechPaths" 
            :key="'tech-path-' + index"
            class="reference-item" @click="handleTechPathClick(techPath)"
          >
            <div class="reference-icon">📋</div>
            <div class="reference-content">{{ techPath.title }}</div>
            <div class="reference-arrow">→</div>
          </div>
          <!-- 显示选中的专利 -->
          <div 
            v-for="(patent, index) in selectedPatentsData" 
            :key="'patent-' + index"
            class="reference-item" @click="handlePatentClick(patent)"
          >
            <div class="reference-icon">📄</div>
            <div class="reference-content">{{ patent.title }} ({{ getPatentNumber(patent) || '编号缺失' }})</div>
            <div class="reference-arrow">→</div>
          </div>
        </div>
      </div>
      
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import {
  ArrowLeft,
  Refresh,
  Loading
} from '@element-plus/icons-vue'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import '@wangeditor/editor/dist/css/style.css'
import { createEditor } from '@wangeditor/editor-for-vue'
import { ElMessage } from 'element-plus'

// 报告章节内容是否已是 HTML（模型偶尔直接输出 HTML，或用户编辑保存后 content 是 wangEditor 的 HTML）
const isHtmlContent = (content) => {
  if (!content) return false
  return /<[a-zA-Z][^>]*>/.test(String(content))
}

// 显示前规整内容：模型正文常"每句一行/空行分隔"，先折叠为连续文本，
// 再按句号/分号/问号/叹号断句；编号条目（"1) xxx" "2. xxx" "3、xxx" 等）单独成段，
// 其余句子每 3 句合成一段，正文紧凑、编号不粘连
const normalizeReportContent = (content) => {
  if (!content) return ''
  const s = String(content).trim()
  if (!s) return ''
  // 去掉所有换行与空行（模型的行内换行是随意的，不应保留为视觉空行）；
  // 同时剥掉行首列表符号（-、•、*），避免折叠后残留
  const flat = s
    .split(/\r?\n/)
    .map(line => line.replace(/^\s*[-•*]\s+/, ''))
    .join('')
    .replace(/[ \t]+/g, ' ')
    .trim()
  if (!flat) return ''
  // 标签冒号后的编号（如 "缓解措施：(1)…"、":一、…"、":（一）…"）另起一行，与后续编号对齐；
  // 空格/冒号后紧跟的数字编号（"关键参数 1. 内层壁"、"包括：1) 利用"）同样视为条目起点另起一行
  // （编号后须跟空格或中文，避免误拆 0.3mm、±0.05mm 这类小数/参数）
  const withBreaks = flat
    .replace(/([：:])\s*(?=(?:[（(](?:\d{1,2}|[一二三四五六七八九十]+)[)）]|[①②③④⑤⑥⑦⑧⑨⑩]|[一二三四五六七八九十百]+、))/g, '$1\n')
    .replace(/(?<=[\s：:])(\d{1,2}[\.、．)）])(?=[\s\u4e00-\u9fff])/g, '\n$1')
  // 按句号/分号/问号/叹号断句；同时把前面插入的 \n（编号/标签另起行标记）也作为硬分隔
  const sentences = withBreaks.split(/(?<=\n|[。！？；])\s*/).filter(Boolean)
  if (sentences.length <= 1) return flat
  // 所有编号/标记开头的句子作为新段起点，避免粘连上一段：
  // 【风险1】、（一）、一、二、 (1)、1)、2.、3、 ①、② 等
  const numberedStart = /^(?:【[^】]+】|[（(](?:\d{1,2}|[一二三四五六七八九十]+)[)）]|\d{1,2}[\.、．)）]|[一二三四五六七八九十百]+、|[①②③④⑤⑥⑦⑧⑨⑩])/
  // 以"："结尾的短标签句（如 部件连接与实施步骤：、系统组成：、关键参数：）也作为新段起点，避免与上一段粘连
  const labelStart = /^.{1,20}[：:]\s*$/
  // 以"措施/原因/风险描述"等标签开头的句子（如 缓解措施：xxx）作为新段起点，避免风险与措施粘连
  const labelPrefixStart = /^(?:缓解措施|解决措施|预防措施|改进措施|应对措施|治理措施|原因分析|风险描述|主要风险|技术指标|验收标准)[：:]/
  const paras = []
  let current = ''
  for (const raw of sentences) {
    const sent = raw.trim()
    if ((numberedStart.test(sent) || labelStart.test(sent) || labelPrefixStart.test(sent)) && current) {
      paras.push(current)
      current = sent
    } else {
      current = current ? current + sent : sent
    }
  }
  if (current) paras.push(current)
  return paras.join('\n\n')
}

// HTML 内容转纯文本（剥掉 <p>/<br>/<span> 等标签和实体，保留文字），再走统一折叠逻辑
const toPlainText = (html) => {
  if (!html) return ''
  return String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .trim()
}

// 风险与缓解措施章节：把【风险N】或 "N. /（N）/①" 编号 + 缓解措施 转为 "1. 风险描述 / 缩进的缓解措施" 结构
const formatRiskSection = (content) => {
  if (!content) return ''
  const s = toPlainText(String(content)).replace(/\r?\n+/g, ' ').replace(/[ \t]+/g, ' ').trim()
  if (!s) return s
  // 风险条目边界：【风险N】、或 句首编号 "1. / 2、 / （3） / ①"（编号前允许 冒号/句号/分号/空格/开头，编号后不跟数字避免误拆 2.5mm 小数）
  const boundary = /(?:(?:【\s*风险\s*\d+\s*】\s*[：:]?\s*)|(?:(?<=^|[。！？；：\s])\d{1,2}[\.、．)）](?!\d)\s*)|(?:(?<=^|[。！？；：\s])[①②③④⑤⑥⑦⑧⑨⑩]\s*))/g
  const matches = [...s.matchAll(boundary)]
  if (matches.length === 0) {
    // 没有识别到风险条目编号：退回通用分组（编号仍会另起一段）
    return normalizeReportContent(s)
  }
  // 第一个编号之前的内容作为引导段
  const intro = s.slice(0, matches[0].index).trim()
  // 每个编号之后到下一个编号之前的内容作为一个风险块
  const blocks = matches.map((m, i) => {
    const start = m.index + m[0].length
    const end = i + 1 < matches.length ? matches[i + 1].index : s.length
    return s.slice(start, end).trim()
  }).filter(Boolean)
  const lis = blocks.map((block) => {
    const mi = block.search(/(缓解措施|解决措施|应对措施|预防措施|改进措施)\s*[：:]/)
    let riskText = block
    let measureText = ''
    if (mi >= 0) {
      riskText = block.slice(0, mi).trim()
      measureText = block.slice(mi).trim()
    }
    let measureHtml = ''
    if (measureText) {
      // 拆出标签（缓解措施：）与正文，flex 布局让续行与正文首字对齐（悬挂缩进）
      const labelMatch = measureText.match(/^(缓解措施|解决措施|应对措施|预防措施|改进措施)\s*[：:]\s*/)
      const label = labelMatch ? labelMatch[0].trim() : ''
      const content = measureText.slice(labelMatch ? labelMatch[0].length : 0).trim()
      measureHtml = `<div style="padding-left:2em;margin-top:4px;color:#555;display:flex;"><span style="flex-shrink:0;">${label}</span><span>${content}</span></div>`
    }
    return `<li>${riskText}${measureHtml}</li>`
  })
  const ol = `<ol style="padding-left:1.2em;margin:0;">${lis.join('')}</ol>`
  return intro ? `${intro}\n\n${ol}` : ol
}

// 只读展示入口已移除（章节始终可编辑），displayContent 不再被调用
const enterLoading = ref(false)

// 是否有实际方案内容（刷新后未恢复出内容时禁用"进入成果库生成"）
const hasSolutionContent = computed(() =>
  Array.isArray(localSolutionSections.value) &&
  localSolutionSections.value.some(s => s && s.content && String(s.content).trim())
)
// 组件接收的props
const props = defineProps({
  //是否显示参考（父组件用 0/1/2 数值控制：0=不动作，1=重新生成，2=展示已存方案）
  isGetGenerate: {
    type: [Boolean, Number],
    default: 0
  },
  // 选中的技术路径数组
  selectedTechPaths: {
    type: Array,
    default: () => []
  },
  // 选中的专利数组
  selectedPatentsData: {
    type: Array,
    default: () => []
  },
  reproductionData: {
    type: Object,
    default: () => ({})
  },
  // 解决方案内容（可选，用于直接传入已生成的内容）
  solutionSections: {
    type: Array,
    default: () => [
      {
        key: 'background',
        title: '背景与问题描述',
        content: ''
      },
      {
        key: 'coreTech',
        title: '核心技术逻辑',
        content: ''
      },
      {
        key: 'structure',
        title: '结构方案与实施要点',
        content: ''
      },
      {
        key: 'parameters',
        title: '关键参数要求',
        content: ''
      },
      {
        key: 'engineering',
        title: '工程落地要点',
        content: ''
      },
      {
        key: 'risk',
        title: '风险与缓解措施',
        content: ''
      }
    ]
  }
})

// 编辑状态管理已移除：章节始终可编辑，不需要单独的编辑/保存按钮

// 富文本编辑器配置 - 只保留图片粘贴功能
const toolbarConfig = {
  // 不显示任何工具栏按钮
  toolbarKeys: []
} 
const showReference = ref(false)

const sanitizeSections = (list) => (Array.isArray(list) ? list.filter(s => s && s.key !== 'reference') : [])

const editorConfig = {
  placeholder: '请输入内容...',
  height: 300, // 设置编辑器高度
  scroll: true, // 启用滚动
  // 只允许粘贴图片
  pasteFilterStyle: false,
  pasteIgnoreImg: false
}

// 自定义粘贴处理函数
const customPaste = (editor, event) => {
  const html = event.clipboardData.getData('text/html')
  const text = event.clipboardData.getData('text/plain')
  const files = Array.from(event.clipboardData.files || [])
  
  // 处理图片文件
  const imageFiles = files.filter(file => file.type.startsWith('image/'))
  if (imageFiles.length > 0) {
    // 插入图片
    imageFiles.forEach(file => {
      const reader = new FileReader()
      reader.onload = e => {
        const imgSrc = e.target.result
        editor.dangerouslyInsertHtml(`<img src="${imgSrc}" alt="粘贴的图片" />`)
      }
      reader.readAsDataURL(file)
    })
    // 阻止默认粘贴行为
    event.preventDefault()
    return false
  }
  
  // 只允许粘贴纯文本，不允许粘贴HTML
  if (html && html !== text) {
    // 阻止HTML粘贴
    event.preventDefault()
    // 只插入纯文本
    editor.insertText(text)
    return false
  }
  
  // 允许纯文本粘贴
  return true
}

// 方案内容数据（使用props传入或从API获取）
// 本地维护的方案内容，用于编辑
const localSolutionSections = ref(Array.isArray(props.solutionSections) ? sanitizeSections(props.solutionSections) : [
  { key: 'background', title: '背景与问题描述', content: '' },
  { key: 'coreTech', title: '核心技术逻辑', content: '' },
  { key: 'structure', title: '结构方案与实施要点', content: '' },
  { key: 'parameters', title: '关键参数要求', content: '' },
  { key: 'engineering', title: '工程落地要点', content: '' },
  { key: 'risk', title: '风险与缓解措施', content: '' }
])

// 加载状态
const isLoading = ref(false)

// 错误信息
const errorMessage = ref('')

// 已等待秒数（生成期间每 1 秒自增，避免用户误以为页面卡死）
const elapsedSeconds = ref(0)

// 前端等待上限：300 秒。
// 服务端总预算为 270 秒（见 server/routes/api/report.js），留 30 秒余量，
// 保证服务端能先返回明确错误，而不是被前端硬中断、留下一个仍在跑的模型请求。
const REQUEST_TIMEOUT_MS = 300 * 1000

// 生成报告的函数
const generateReport = async () => {
  // 检查是否至少有一个数据
  if ((!props.selectedTechPaths || props.selectedTechPaths.length === 0) && 
      (!props.selectedPatentsData || props.selectedPatentsData.length === 0)) {
    errorMessage.value = '技术路径内容和专利信息不能为空'
    return
  }
  
  isLoading.value = true
  errorMessage.value = ''
  elapsedSeconds.value = 0
  
  // 300 秒超时控制：模型慢/无响应时明确提示，不再无限等待
  const controller = new AbortController()
  const timeoutTimer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  const elapsedTimer = setInterval(() => { elapsedSeconds.value += 1 }, 1000)
  
  try {
    const response = await fetch('/api/report/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        techPathContent: props.selectedTechPaths,
        patentInfo: props.selectedPatentsData
      }),
      signal: controller.signal
    })
    
    if (!response.ok) {
      // 后端返回明确错误（含模型超时/空返回/解析失败），透传给用户
      let errMsg = `API请求失败，状态码: ${response.status}`
      try {
        const errData = await response.json()
        if (errData && errData.error) errMsg = errData.error
      } catch (e) { /* 忽略解析失败 */ }
      throw new Error(errMsg)
    }
    
    const data = await response.json()
    console.log('生成的报告内容:', data)
    
    if (data.solutionSections && Array.isArray(data.solutionSections) && data.solutionSections.length > 0) {
      localSolutionSections.value = sanitizeSections(data.solutionSections)
      // 通知父组件解决方案生成完成
      emit('solution-generated', localSolutionSections.value)
      // 生成成功：允许进入成果库生成（按钮由 enterLoading 控制启用）
      enterLoading.value = true
    } else {
      throw new Error('方案生成失败：模型未产出有效内容')
    }
  } catch (error) {
    console.error('生成报告失败:', error)
    if (error.name === 'AbortError') {
      errorMessage.value = '方案生成超时（等待超过 5 分钟），请点击"重新生成"重试'
    } else {
      errorMessage.value = error.message || '方案生成失败，请重试'
    }
    // 失败时不进入成果库
    enterLoading.value = false
  } finally {
    clearTimeout(timeoutTimer)
    clearInterval(elapsedTimer)
    isLoading.value = false
    showReference.value = true
  }
}

// // 监听props变化，自动生成报告
// watch(
//   [() => props.selectedTechPaths, () => props.selectedPatentsData],
//   ([newSelectedTechPaths, newSelectedPatentsData]) => {
//     // 只要其中一个有值就生成报告
//     console.log('newSelectedTechPaths:', newSelectedTechPaths)
//     console.log('newSelectedPatentsData:', newSelectedPatentsData)
//     if ((newSelectedTechPaths && newSelectedTechPaths.length > 0) || 
//         (newSelectedPatentsData && newSelectedPatentsData.length > 0)) {
//       console.log('技术路径或专利数据变化，生成报告')
//       generateReport()
//     }
//   },
//   { immediate: true, deep: true }
// )
// watch(() => props.reproductionData, (newReproductionData) => {
//   if (newReproductionData && newReproductionData.solutionSections && Array.isArray(newReproductionData.solutionSections)) {
//     // 如果有分析结果，更新方案内容
//     localSolutionSections.value = newReproductionData.solutionSections
//     enterLoading.value = true
//   }
// })
watch(() => props.isGetGenerate, (newIsGetGenerate) => {
  console.log('newIsGetGenerate:', newIsGetGenerate)
  if (newIsGetGenerate==1) {
    generateReport()
  }else if(newIsGetGenerate==2){
    localSolutionSections.value = sanitizeSections(props.solutionSections)
    enterLoading.value = true
    showReference.value = true
  }
})

// 富文本编辑器实例管理
const editorInstances = ref({})
const editorContents = ref({})

// 自动保存：内容变化后防抖上报父组件。
// 父组件的 content-updated 会触发「上传项目 + 结构化落库」两次请求，不能每次按键都发。
let saveTimer = null
const scheduleSave = () => {
  clearTimeout(saveTimer)
  saveTimer = setTimeout(() => {
    emit('content-updated', localSolutionSections.value)
  }, 800)
}
// 卸载时清掉未触发的自动保存定时器
onBeforeUnmount(() => clearTimeout(saveTimer))

// 章节载入时先给每个编辑器准备初始内容（v-model 需要该 key 提前存在）
watch(localSolutionSections, (sections) => {
  sections.forEach(section => {
    if (editorContents.value[section.key] === undefined) {
      editorContents.value[section.key] = section.content || ''
    }
  })
}, { immediate: true })

// 编辑器内容变化处理：章节始终可编辑，改动直接写回并触发自动保存
const handleEditorChange = (key, editor) => {
  const html = editor.getHtml()
  editorContents.value[key] = html
  // 更新到solutionSections中
  const index = localSolutionSections.value.findIndex(section => section.key === key)
  if (index !== -1) {
    localSolutionSections.value[index].content = html
  }
  scheduleSave()
}

// 编辑器销毁前处理
const handleEditorBeforeDestroy = (key, editor) => {
  // 保存最后的内容
  if (editor) {
    editorContents.value[key] = editor.getHtml()
    const index = localSolutionSections.value.findIndex(section => section.key === key)
    if (index !== -1) {
      localSolutionSections.value[index].content = editor.getHtml()
    }
  }
}

// 定义组件发射的事件
const emit = defineEmits(['back-to-edit', 'enter-achievement-library', 'back', 'solution-generated', 'content-updated'])

// 返回按钮点击事件
const back = () => {
  showReference.value = false
  enterLoading.value = false
  emit('back')
}

// 复制按钮已移除（用户明确不要该入口）
// 专利编号兜底：当次会话的对象有 p/pn，刷新后从数据库恢复的对象只有 externalId
const getPatentNumber = (patent) => patent?.pn || patent?.p || patent?.externalId || ''
const handlePatentClick = (patent) => {
  console.log('点击了专利:', patent)
  const pn = getPatentNumber(patent)
  if (!pn) {
    ElMessage.warning('该专利缺少编号，暂时无法打开详情')
    return
  }
  window.open(`/pdf?id=${encodeURIComponent(pn)}`, '_blank', 'noopener,noreferrer')
}
const handleTechPathClick = (techPath) => {
  console.log('点击了技术路径:', techPath)
  // 详情接口只接受数据库正式ID；模型生成的 techPath.id 是临时编号，不能作为回退值。
  const pathId = techPath._dbId || ''
  if (!pathId) {
    ElMessage.warning('该技术路径尚未完成保存，请返回分析结果后重试')
    return
  }
  const url = `/solution?pathId=${encodeURIComponent(pathId)}`
  window.open(url, '_blank', 'noopener,noreferrer')
}
</script>

<style scoped lang="scss">
.generated-solution {
  display: flex;
  flex-direction: column;

  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  margin: 20px 0px;
  border-radius: 8px;
  overflow: hidden;
}

/* 页面标题 */
.page-header {
  padding: 20px 30px;
  border-bottom: 1px solid #e8e8e8;
  
  h2 {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

/* 顶部导航 */
.top-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 30px;
  background-color: #ffffff;
  border-bottom: 1px solid #e8e8e8;
  
  :deep(.el-breadcrumb) {
    font-size: 14px;
    color: #666;
    
    a {
      color: #666;
      text-decoration: none;
      
      &:hover {
        color: #1890ff;
      }
    }
  }
}

.enter-btn {
  padding: 6px 16px;
  font-size: 14px;
  background-color: #1890ff;
  border-color: #1890ff;
  
  &:hover {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
}

/* 方案内容区域 */
.solution-content {
  padding: 30px;
  overflow-y: auto;
  background-color: #ffffff;
}

/* 加载状态 */
.loading-container {
  padding: 40px 20px;
}

.loading-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 24px;
  text-align: center;

  &__icon {
    font-size: 28px;
    color: #1890ff;
    animation: spin 0.8s linear infinite;
  }

  &__title {
    margin: 4px 0 0;
    font-size: 16px;
    font-weight: 500;
    color: #333;
  }

  &__hint {
    margin: 0;
    font-size: 13px;
    color: #909399;
    line-height: 1.5;
  }

  &__elapsed {
    margin: 2px 0 0;
    font-size: 13px;
    color: #606266;
    font-variant-numeric: tabular-nums;
  }
}

/* 错误提示区 */
.error-container {
  padding: 40px 20px;
  text-align: center;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.error-actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 12px;
}

/* 方案章节 */
.solution-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: transparent;
  position: relative;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

/* 标题对齐《研发立项报告-样例.docx》的一级标题：14pt / 加粗 / #0F4761 / 黑体 + 底部细线 */
.section-title {
  font-family: '黑体', SimHei, 'Microsoft YaHei', sans-serif;
  font-size: 14pt;
  font-weight: bold;
  color: #0F4761;
  border-bottom: 1px solid #c9d6e2;
  padding-bottom: 8px;
  margin: 0;
}

.section-text {
  margin-bottom: 16px;
}

/* 文本域样式 */
.solution-textarea {
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  resize: vertical;
  line-height: 1.6;
  font-family: inherit;
  background-color: #fafafa;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    background-color: #ffffff;
  }
  
  &::placeholder {
    color: #909399;
  }
}

/* 编辑器容器样式（外框已去掉，正文直接平铺） */
.editor-container {
  :deep(.w-e-toolbar) {
    border-bottom: 1px solid #dcdfe6;
    background-color: #fafafa;
  }
  
  :deep(.w-e-text-container) {
    background-color: #ffffff;
  }
  
  :deep(.w-e-text) {
    font-size: 14px;
    color: #333;
    line-height: 1.6;
  }
}

/* 章节操作按钮（编辑/复制）已移除：章节始终可编辑，改动自动保存 */

/* 参考方案和专利样式 */
.reference-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* 与章节标题保持一致，避免同一页出现两种标题样式 */
.reference-title {
  font-family: '黑体', SimHei, 'Microsoft YaHei', sans-serif;
  font-size: 14pt;
  font-weight: bold;
  color: #0F4761;
  border-bottom: 1px solid #c9d6e2;
  padding-bottom: 8px;
  margin: 0 0 16px 0;
}

.reference-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reference-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
}

.reference-icon {
  font-size: 18px;
  margin-right: 12px;
  width: 24px;
  text-align: center;
}

.reference-content {
  flex: 1;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.reference-arrow {
  font-size: 16px;
  color: #999;
  margin-left: 12px;
}
</style>